import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import teamCopy from '@/i18n/ns/team';
import type { TeamData, TeamPerson } from './team-api';

type Invitation = TeamData['invitations'][number];

export type TeamConfirmation =
  | { person: TeamPerson; action: 'remove' | 'transfer' }
  | { invitation: Invitation; action: 'revoke' };

/**
 * Asks before removing a person, handing them ownership, or revoking an
 * invitation. The confirm names the person and the act (DESIGN-SPEC §6.10).
 * Removing and revoking are destructive; a transfer is the dialog's one primary.
 */
export function TeamConfirmDialog({
  confirmation,
  busy,
  onCancel,
  onConfirm,
}: {
  confirmation: TeamConfirmation;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const copy = useNamespaceCopy(teamCopy);
  const shared = { busy, onCancel, onConfirm };
  if (confirmation.action === 'revoke')
    return (
      <ConfirmDialog
        {...shared}
        title={copy.revokeTitle}
        description={formatCopy(copy.revokeDescription, {
          email: confirmation.invitation.email,
        })}
        confirmLabel={copy.revokeConfirm}
      />
    );
  const { person, action } = confirmation;
  const remove = action === 'remove';
  const values = { email: person.email, name: person.displayName || person.email };
  return (
    <ConfirmDialog
      {...shared}
      tone={remove ? 'danger' : 'default'}
      title={remove ? copy.removeTitle : copy.transferTitle}
      description={formatCopy(remove ? copy.removeDescription : copy.transferDescription, values)}
      confirmLabel={formatCopy(remove ? copy.removeConfirm : copy.transferConfirm, values)}
    />
  );
}
